/**
 * Teaches Jodit's "video" toolbar button to embed direct media URLs.
 *
 * Out of the box the button only understands YouTube and Vimeo - its own tooltip
 * says so, and convertMediaUrlToVideoEmbed() (jodit/core/helpers/utils) switches
 * on the hostname and RETURNS THE URL UNCHANGED for anything else. So pasting a
 * direct link such as
 *
 *     https://arise-static-content.s3.ap-south-1.amazonaws.com/Q132.mp4
 *
 * dropped the bare string into the question as plain text instead of a player.
 *
 * We can't hook the conversion in place: jodit's video control captures the
 * helper at import time (plugins/video/config.ts), so the whole `popup` has to be
 * re-registered. The replacement keeps both of the stock inputs - a URL field and
 * an embed-code field - but stacks them instead of using TabsWidget, which jodit
 * 3.24 does not expose on `Jodit.modules`.
 *
 * Imported for its side effect from src/index.js, before <App/> mounts, so every
 * editor in the portal (Add/Edit Question, the QB edit modal, Answer Conflicts,
 * Test Upload Review, Question Variations, Feedback replies) picks it up.
 *
 * The resulting <video> survives all the way to the students: question HTML goes
 * through bypassSecurityTrustHtml in arise-mobileapp (custom-html.pipe.ts) and
 * raw dangerouslySetInnerHTML in arise-student-portal, so neither strips it.
 */
import { Jodit } from 'jodit-react';

const { UIForm, UIInput, UITextArea, UIBlock, UIButton, Helpers } = Jodit.modules;

// Extensions a browser can play from a plain <video>/<audio> element. Deliberately
// conservative - .mkv, .avi, .wmv and .flv are excluded because no browser plays
// them natively and an empty player is worse than a link.
const VIDEO_EXTENSIONS = ['mp4', 'm4v', 'webm', 'ogv', 'mov'];
const AUDIO_EXTENSIONS = ['mp3', 'm4a', 'aac', 'wav', 'oga'];

/**
 * Extension of the path portion only - query strings (?X-Amz-Signature=...) and
 * fragments would otherwise swallow it on pre-signed S3 links.
 */
const fileExtension = (url) => {
  const path = url.split('#')[0].split('?')[0];
  const dot = path.lastIndexOf('.');
  const slash = path.lastIndexOf('/');
  return dot > slash ? path.slice(dot + 1).toLowerCase() : '';
};

// A <video> with preload="metadata" and no poster paints as an unlabelled empty
// rectangle until the first frame arrives - in the editor and in the student apps
// alike. The border + dark fill make it read as "a player lives here" while it is
// still loading. Inline rather than a stylesheet rule because this HTML is stored
// in the question and rendered by three different apps, none of which share CSS.
const VIDEO_STYLE =
  'max-width:100%;display:block;border:1px solid #c8ccd4;border-radius:6px;background:#000';

// Audio already draws its own full-width player chrome, so it only needs the
// outline - a black fill would just clash with the native controls.
const AUDIO_STYLE =
  'max-width:100%;display:block;border:1px solid #c8ccd4;border-radius:6px';

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/**
 * URL -> HTML to insert. Exported so it can be unit tested without an editor.
 */
export const mediaUrlToEmbedHtml = (rawUrl) => {
  const url = String(rawUrl || '').trim();

  if (!url) {
    return '';
  }

  // YouTube / Vimeo keep jodit's own iframe markup - no reason to diverge.
  const joditEmbed = Helpers.convertMediaUrlToVideoEmbed(url);

  if (joditEmbed !== url) {
    return joditEmbed;
  }

  const extension = fileExtension(url);
  const src = escapeHtml(url);

  if (VIDEO_EXTENSIONS.indexOf(extension) !== -1) {
    return (
      `<video src="${src}" controls playsinline preload="metadata"` +
      ` style="${VIDEO_STYLE}"></video>`
    );
  }

  if (AUDIO_EXTENSIONS.indexOf(extension) !== -1) {
    return `<audio src="${src}" controls preload="metadata" style="${AUDIO_STYLE}"></audio>`;
  }

  // Not something a browser can play. Insert a working link rather than jodit's
  // bare text - an unclickable URL in the middle of a question helps nobody.
  return `<a href="${src}" target="_blank" rel="noopener noreferrer">${src}</a>`;
};

const insertButton = (editor, onAction) => {
  const button = new UIButton(editor, {
    text: 'Insert',
    variant: 'primary',
    tabIndex: editor.o.allowTabNavigation ? 0 : -1,
  });

  button.onAction(onAction);

  return button;
};

Jodit.defaultOptions.controls.video = {
  ...Jodit.defaultOptions.controls.video,

  // 'video'/'audio' added so the button reads as active when the caret sits on a
  // player we inserted, the way it already did for YouTube/Vimeo iframes.
  tags: ['iframe', 'video', 'audio'],
  tooltip: 'Insert video (YouTube, Vimeo or a direct media link)',

  popup: (editor, current, control, close) => {
    const insert = (html) => {
      if (!html) {
        return;
      }

      editor.s.restore();
      editor.s.insertHTML(html);
      close();
    };

    const urlForm = new UIForm(editor, [
      new UIBlock(editor, [
        new UIInput(editor, {
          name: 'url',
          required: true,
          label: 'URL',
          placeholder: 'https://',
          validators: ['url'],
        }),
      ]),
      new UIBlock(editor, [insertButton(editor, () => urlForm.submit())]),
    ]);

    urlForm.onSubmit((data) => insert(mediaUrlToEmbedHtml(data.url)));

    const codeForm = new UIForm(editor, [
      new UIBlock(editor, [
        new UITextArea(editor, {
          name: 'code',
          required: true,
          label: 'Embed code',
        }),
      ]),
      new UIBlock(editor, [insertButton(editor, () => codeForm.submit())]),
    ]);

    codeForm.onSubmit((data) => insert(data.code));

    // Snapshot the selection before the popup steals focus, so insert() can put
    // the player back where the caret was. Mirrors jodit's own control.
    editor.s.save();

    const box = editor.c.div('jodit-media-embed');
    box.appendChild(urlForm.container);
    box.appendChild(codeForm.container);

    return box;
  },
};
